// src/pages/basket/components/BasketTable.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { useLanguage } from "../../../contexts/LanguageContext";
import { getCurrencySymbol } from "../../../utils/currencySymbol";

const BasketTable = ({
  basket,
  onUpdateQuantity,
  onUpdateDeliveryDate,
  onRemoveItem,
  onClearBasket,
  onConvertToOrder,
  totalRecords,
  isLoading,
  onPage,
  lazyParams,
  resetPagination = false,
  refetchBasket,
}) => {
  const { th } = useLanguage();
  const toast = useRef(null);

  const [uiQty, setUiQty] = useState({});
  const getShownQty = (row) => (uiQty[row.itemCode] ?? row.quantity ?? 1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [currentFirst, setCurrentFirst] = useState(0);

  // Bülten siparişi kontrolü
  const isServiceBulletinOrder = basket?.bulletNumber != null && basket?.bulletNumber !== "";

  const safeParams = lazyParams || { page: 1, rows: 10 };

  useEffect(() => {
    if (resetPagination) {
      setCurrentFirst(0);
      setSortField(null);
      setSortOrder(null);
    }
  }, [resetPagination]);

  const handlePage = (event) => {
    setCurrentFirst(event.first);
    onPage?.(event);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const discountedUnit = (row) => {
    const qty = Number(row.quantity || 1);
    const base =
      row.netAmount != null
        ? Number(row.netAmount)
        : Number(row.amount ?? 0) - Number(row.discount ?? 0);
    return qty > 0 ? base / qty : 0;
  };

  const discountedLine = (row) => {
    if (row.netAmount != null) return Number(row.netAmount);
    return Number(row.amount ?? 0) - Number(row.discount ?? 0);
  };

  const handleQuantityChange = async (row, nextQty) => {
    const desired = Number(nextQty ?? 1);
    const serverQty = Number(row.quantity ?? 1);
    const limit = Number(row.internalOrderLimit);

    setUiQty((m) => ({ ...m, [row.itemCode]: desired }));

    try {
      await onUpdateQuantity(row.itemCode, desired);
      setUiQty((m) => {
        const { [row.itemCode]: _ignored, ...rest } = m;
        return rest;
      });
      refetchBasket?.();
    } catch {
      setUiQty((m) => ({ ...m, [row.itemCode]: serverQty }));
      refetchBasket?.();
    }
  };

  const quantityTemplate = (row) => {
    const disabled = isLoading || isServiceBulletinOrder;
    return (
      <div className="flex align-items-center gap-1">
        <InputNumber
          value={getShownQty(row)}
          onValueChange={(e) => handleQuantityChange(row, e.value)}
          showButtons
          min={1}
          disabled={disabled}
          inputStyle={{ width: "60px", height: "30px", textAlign: "center" }}
          pt={{
            incrementButton: { style: { height: "15px", padding: 0 } },
            decrementButton: { style: { height: "15px", padding: 0 } },
          }}
        />
        {isServiceBulletinOrder && (
          <>
            <i
              id={`qty-tooltip-${row.itemCode}`}
              className="pi pi-info-circle text-color-secondary"
              style={{ cursor: "help" }}
            />
            <Tooltip target={`#qty-tooltip-${row.itemCode}`} content={th("basket.serviceBulletinOrderRestriction")} />
          </>
        )}
      </div>
    );
  };

  const deliveryDateTemplate = (row) => {
    if (!basket?.isDomestic) {
      return null;
    }

    const disabled = isLoading;
    const currentDate = row.deliveryDate ? new Date(row.deliveryDate) : new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <Calendar
        value={currentDate}
        onChange={(e) => {
          if (e.value && onUpdateDeliveryDate) {
            onUpdateDeliveryDate(row.basketLineId, e.value);
          }
        }}
        minDate={today}
        dateFormat="dd/mm/yy"
        showIcon
        disabled={disabled}
        style={{ width: "150px" }}
      />
    );
  };

  const actionTemplate = (row) => {
    const disabled = isLoading || isServiceBulletinOrder;
    return (
      <div className="flex align-items-center gap-1">
        <Button
          icon="pi pi-trash"
          severity="danger"
          onClick={() => onRemoveItem(row.itemCode)}
          disabled={disabled}
          loading={isLoading}
        />
        {isServiceBulletinOrder && (
          <>
            <i
              id={`delete-tooltip-${row.itemCode}`}
              className="pi pi-info-circle text-color-secondary"
              style={{ cursor: "help" }}
            />
            <Tooltip target={`#delete-tooltip-${row.itemCode}`} content={th("basket.serviceBulletinOrderRestriction")} />
          </>
        )}
      </div>
    );
  };

  const sortedData = useMemo(() => {
    if (!sortField || !basket?.lines) return basket?.lines || [];
    return [...basket.lines].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal, "tr", { numeric: true });
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return (sortOrder || 1) * comparison;
    });
  }, [basket?.lines, sortField, sortOrder]);

  if (!basket || basket?.lines?.length === 0) {
    return (
      <div
        className="card d-flex align-items-center justify-content-center p-10"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <i className="pi pi-shopping-cart" style={{ fontSize: "3rem", color: "#c5c5c5" }} />
          <h4 className="mt-3 text-gray-600">{th("basket.empty")}</h4>
          <Button
            label={th("basket.continueShopping")}
            icon="pi pi-arrow-right"
            className="p-button-text mt-2"
            onClick={() => (window.location.href = "/parts/product-search")}
          />
        </div>
      </div>
    );
  }

  const footerTotal =
    basket.netAmount ??
    basket.totalSubAmount ??
    (Number(basket.totalAmount ?? 0) - Number(basket.totalDiscount ?? 0));

  return (
    <div>
      <Toast ref={toast} />

      <DataTable
        value={sortedData}
        paginator
        first={currentFirst}
        rows={safeParams.rows}
        totalRecords={totalRecords}
        loading={isLoading}
        onPage={handlePage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        rowsPerPageOptions={[10, 20, 50]}
        dataKey="id"
        showGridlines
        stripedRows
        responsiveLayout="scroll"
        scrollable
        emptyMessage={th("basket.empty")}
        removableSort
        sortMode="single"
      >
        <Column field="itemCode" header={th("product.code")} sortable />
        <Column field="partName" header={th("basket.partName")} sortable />
        <Column field="uom" header={th("basket.uom")} sortable />
        <Column field="stockQuantity" header={th("basket.stockQuantity")} sortable />
        <Column header={th("basket.quantity")} body={quantityTemplate} />
        {basket?.isDomestic && (
          <Column header={th("basket.deliveryDate")} body={deliveryDateTemplate} />
        )}
        <Column field="unitRetailPrice"
          header={th("basket.unitPrice")}
          body={(row) => `${Number(row.unitRetailPrice ?? 0).toFixed(2)} ${getCurrencySymbol(row.currency)}`}
          sortable />
        <Column field="totalUnitRetailPrice"
          header={th("basket.totalUnitPrice")}
          body={(row) => `${Number(row.totalUnitRetailPrice ?? 0).toFixed(2)} ${getCurrencySymbol(row.currency)}`}
          sortable />
        <Column field="dealerDiscountedAmount"
          header={th("basket.dealerDiscountedAmount")}
          body={(row) => `${Number(row.dealerDiscountedAmount ?? 0).toFixed(2)} ${getCurrencySymbol(row.currency)}`}
          sortable />
        <Column field="netAmount"
          header={th("basket.netTotal")}
          body={(row) => `${Number(row.netAmount ?? 0).toFixed(2)} ${getCurrencySymbol(row.currency)}`}
          sortable />
        <Column header={th("basket.actions")} body={actionTemplate} />
      </DataTable>

      <div className="mt-4 d-flex justify-content-end">
        <strong  style={{ color: '#003366', fontSize: '1.2rem' }}>
          {th("basket.totalAmount")}:
          {" "}
          {Number(basket?.netAmount ?? 0).toFixed(2)} {getCurrencySymbol(basket?.currency)}
        </strong>
      </div>
    </div>
  );
};

export default BasketTable;
